import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../context/AuthProvider";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import { useNetwork } from "../../../context/NetworkContext";
import DateConverter from "@heartinz/date_and_time_processor";
import handleError from "../../../constants/handleError";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import CardPreLoader from "../dashbord/CardPreLoader";
import uRLs from "../../../utils/baseUtils";
import MonthlySaleAnalytics from "./sales/MonthlySaleAnalytics";
import SaleAnalytics from "./sales/SaleAnalytics";
import WeeklySale from "./sales/WeeklySale";
import { FilterAltIcon, RefreshIcon } from "../../../constants/Icons";

function Analytics() {

  const { isUser } = useAuth();
  const showAlert = useShowAlert();
  const { checkNetwork } = useNetwork();
   const [fetchStatus, setFetchStatus] = useState({
    sale: false,
    daterangeByHours: false, 
  dateRangeByDays: false,
  });
  const [loadingStatus, setLoadingStatus] = useState({
      byHours: false,
      byDayOfWeek: false,
      isLoading:false
    });  
  const [biWeekData, setBiWeekData] = useState([]);
  const [weeklyHourlyData, setWeeklyHourlyData] = useState([]);
  const [isTitle, setIsTitle] = useState("Today Sales");
  const [AnalyticsData, setAnalyticsData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("daily");
  const [isDayofWeekData,setDayofWeekData]=useState([]);
  const serverTime = GetServerLiveTime();
  const iso = serverTime.toISOString();
  const currentDate = DateConverter.convertToTimeZone(iso, isUser?.timezone);

  const LAST_MONTHLY_FETCH_KEY = "monthlyLastFetchTimestamp";
  const REFRESH_INTERVAL_MINUTES = parseInt(
    uRLs.DASHBOARD_REFRESH_INTERVAL || 30,
    10
  );
  const REFRESH_INTERVAL_MS = REFRESH_INTERVAL_MINUTES * 60 * 1000;
  const LOCAL_STORAGE_KEY = "analyticsLastFetch";
  const CACHE_KEY = "analyticsData";

  const fetchData = useCallback(
    async (filter, forceFetch = false) => {
      const cacheKey =
        filter === "daily"
          ? "daily-sale"
          : filter === "monthly"
          ? "monthly-sale"
          : "weekly-sale";
      const lastFetchKey = LOCAL_STORAGE_KEY + cacheKey;
      const lastFetch = sessionStorage.getItem(lastFetchKey);
      const now = Date.now();

      const cachedData = sessionStorage.getItem(CACHE_KEY);
      const parsedData = cachedData ? JSON.parse(cachedData) : null;
      const cachedResult = parsedData?.[cacheKey];

      const isCacheValid =
        !forceFetch &&
        lastFetch &&
        now - parseInt(lastFetch, 10) < REFRESH_INTERVAL_MS &&
        cachedResult;

      if (isCacheValid) {
        setAnalyticsData(cachedResult);
        setLoadingStatus(prev => ({ ...prev, isLoading: false }));       
        return;
      }
      if (filter === "monthly") {
        sessionStorage.setItem(LAST_MONTHLY_FETCH_KEY, Date.now());
      }
      setLoadingStatus(prev => ({ ...prev, isLoading: true }));
      await new Promise((resolve) => setTimeout(resolve, 1000));

      try {
        if (!checkNetwork()) return;

        let apiUrl = "";

        if (filter === "daily") {
          const fromDate = encodeURIComponent(currentDate);
          apiUrl = `module/analytics/bi-date?date=${fromDate}`;
        } else if (filter === "monthly") {
          apiUrl = `module/analytics/`;
        }

        const response = await AxiosInstance.get(apiUrl);
        const result = response?.data?.results;
        const newCache = { ...(parsedData || {}), [cacheKey]: result };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
        sessionStorage.setItem(lastFetchKey, now.toString());

        setAnalyticsData(result);
     setFetchStatus(prevStatus => {
      const newStatus = { ...prevStatus, sale: true };
      sessionStorage.setItem("fetchStatus", JSON.stringify(newStatus));
      return newStatus;
    });

      } catch (error) {
        handleError(error, showAlert);
      } finally {
        setLoadingStatus(prev => ({ ...prev, isLoading: false }));
      }
    },
    [checkNetwork, currentDate, isUser, showAlert]
  );
  const handleQuickFilter = (filter) => {
    setSelectedFilter(filter);
    if (filter === "weekly") {
      fetchBiWeekData();
    } else {
      fetchData(filter);
    }
  };

  useEffect(() => {
    if (selectedFilter === "daily") {
      setIsTitle("Today Sales");
    } else if (selectedFilter === "monthly") {
      setIsTitle("Monthly Sales");
    } else if (selectedFilter === "weekly") {
      setIsTitle("Weekly Sales by Hour");
    }
  }, [selectedFilter]);

  const handleRefresh = useCallback(() => {
    fetchData(selectedFilter, true);
  }, [selectedFilter, fetchData]);

  const fetchBiWeekData = useCallback(
    async (forceFetch = false) => {
      const cacheKey = "bi-week-sale";
      const lastFetchKey = LOCAL_STORAGE_KEY + cacheKey;
      const lastFetch = sessionStorage.getItem(lastFetchKey);
      const now = Date.now();

      const cachedData = sessionStorage.getItem(CACHE_KEY);
      const parsedData = cachedData ? JSON.parse(cachedData) : {};
      const cachedResult = parsedData?.[cacheKey];

      const isCacheValid =
        !forceFetch &&
        lastFetch &&
        now - parseInt(lastFetch, 10) < REFRESH_INTERVAL_MS &&
        cachedResult;

      if (isCacheValid) {
        setBiWeekData(cachedResult);
        setLoadingStatus(prev => ({ ...prev, isLoading: false }));
      
        return;
      }

      setLoadingStatus(prev => ({ ...prev, isLoading: true }));
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const response = await AxiosInstance.get(`module/analytics/bi-week`);
        const result = response?.data?.results || [];

        const newCache = { ...parsedData, [cacheKey]: result };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
        sessionStorage.setItem(lastFetchKey, now.toString());

        setBiWeekData(result);
      } catch (error) {
        handleError(error, showAlert);
      } finally {
        setLoadingStatus(prev => ({ ...prev, isLoading: false }));
      }
    },
    [checkNetwork, showAlert]
  );

  useEffect(() => {
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    const parsedData = cachedData ? JSON.parse(cachedData) : null;
      const cachedFetchStatus = sessionStorage.getItem("fetchStatus");

     const parsedFetchStatus = cachedFetchStatus ? JSON.parse(cachedFetchStatus) : {};

    const initialCacheKeys = {
      daily: "daily-sale",
      biWeek: "bi-week-sale",
      weeklyHourly: "weekly-hourly-sale",
      weeklyday: "day-of-week-sale",
    };
    let newFetchStatus = { ...parsedFetchStatus };

    if (parsedData?.[initialCacheKeys.daily]) {
      setAnalyticsData(parsedData[initialCacheKeys.daily]);
   newFetchStatus = { ...newFetchStatus, sale: true };
    }

    if (parsedData?.[initialCacheKeys.biWeek]) {
      setBiWeekData(parsedData[initialCacheKeys.biWeek]);
      newFetchStatus = { ...newFetchStatus, sale: true }; 
    }
 if (parsedData?.[initialCacheKeys.weeklyday]) {
      setDayofWeekData(parsedData[initialCacheKeys.weeklyday]);
       newFetchStatus = { ...newFetchStatus, dateRangeByDays: true }; 
    }

    if (parsedData?.[initialCacheKeys.weeklyHourly]) {
      setWeeklyHourlyData(parsedData[initialCacheKeys.weeklyHourly]);
      newFetchStatus = { ...newFetchStatus, daterangeByHours: true }; 
    }

setFetchStatus(newFetchStatus);
  sessionStorage.setItem("fetchStatus", JSON.stringify(newFetchStatus));
  }, []);

  const fetchWeeklyHourlyData = useCallback(
    async (forceFetch = false) => {
      const cacheKey = "weekly-hourly-sale";
      const lastFetchKey = LOCAL_STORAGE_KEY + cacheKey;
      const now = Date.now();

      const cachedData = sessionStorage.getItem(CACHE_KEY);
      const parsedData = cachedData ? JSON.parse(cachedData) : {};
      const cachedResult = parsedData?.[cacheKey];
      const lastFetch = sessionStorage.getItem(lastFetchKey);

      // Check if cache is valid and a forced fetch is not requested
      const isCacheValid =
        !forceFetch &&
        lastFetch &&
        now - parseInt(lastFetch, 10) < REFRESH_INTERVAL_MS &&
        cachedResult;

      if (isCacheValid) {
        setWeeklyHourlyData(cachedResult);
        return;
      }

     setLoadingStatus(prev => ({ ...prev, byHours: true }));
     await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const { startDate, endDate } = DateConverter.getDateRange(
          "thisWeek",
          currentDate
        );
        const fromDateFormatted = encodeURIComponent(startDate);
        const toDateFormatted = encodeURIComponent(endDate);
        const response = await AxiosInstance.get(
          `module/analytics/daterange-by-hour?fromdate=${fromDateFormatted}&todate=${toDateFormatted}`
        );
        const result = response?.data?.results || [];

        const newCache = { ...parsedData, [cacheKey]: result };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
        sessionStorage.setItem(lastFetchKey, now.toString());

        setWeeklyHourlyData(result);
         setDayofWeekData(result);
          setFetchStatus(prevStatus => {
      const newStatus = { ...prevStatus, daterangeByHours: true };
      sessionStorage.setItem("fetchStatus", JSON.stringify(newStatus));
      return newStatus;
    });
      } catch (error) {
        handleError(error, showAlert);
      } finally {
       setLoadingStatus(prev => ({ ...prev, byHours: false }));
      }
    },
    [checkNetwork, currentDate, showAlert]
  );
  
  const fetchDayOfWeekData = useCallback(
    async (forceFetch = false) => {
      const cacheKey = "day-of-week-sale";
      const lastFetchKey = LOCAL_STORAGE_KEY + cacheKey;
      const now = Date.now();

      const cachedData = sessionStorage.getItem(CACHE_KEY);
      const parsedData = cachedData ? JSON.parse(cachedData) : {};
      const cachedResult = parsedData?.[cacheKey];
      const lastFetch = sessionStorage.getItem(lastFetchKey);

    
      const isCacheValid =
        !forceFetch &&
        lastFetch &&
        now - parseInt(lastFetch, 10) < REFRESH_INTERVAL_MS &&
        cachedResult;

      if (isCacheValid) {
        setDayofWeekData(cachedResult);
        return;
      }

setLoadingStatus(prev => ({ ...prev, byDayOfWeek: true }));
await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const { startDate, endDate } = DateConverter.getDateRange(
          "thisWeek",
          currentDate
        );
        const fromDateFormatted = encodeURIComponent(startDate);
        const toDateFormatted = encodeURIComponent(endDate);
        const response = await AxiosInstance.get(
          `module/analytics/daterange-by-dayofweek?fromdate=${fromDateFormatted}&todate=${toDateFormatted}`
        );
        const result = response?.data?.results || [];

        const newCache = { ...parsedData, [cacheKey]: result };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
        sessionStorage.setItem(lastFetchKey, now.toString());

        setDayofWeekData(result);
          setFetchStatus(prevStatus => {
      const newStatus = { ...prevStatus, dateRangeByDays: true };
      sessionStorage.setItem("fetchStatus", JSON.stringify(newStatus));
      return newStatus;
    });
      } catch (error) {
        handleError(error, showAlert);
      } finally {
        setLoadingStatus(prev => ({ ...prev, byDayOfWeek: false }));
      }
    },
    [checkNetwork, currentDate, showAlert]
  );
  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center mb-2">
              <div className="breadcrumb-title pe-3 py-2">Sales Analytics</div>{" "}
              <div className="col-lg-2 col-6 mb-2 mb-lg-0 ms-auto">
                <div className="input-group">
                  <span className="input-group-text">
                    <FilterAltIcon/>
                  </span>
                  <select
                    className="form-select"
                    onChange={(e) => handleQuickFilter(e.target.value)}
                    value={selectedFilter}
                  >
                    <option value="daily">Today</option>
                    <option value="weekly">This Week</option>
                    <option value="monthly">This Month</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 ">
                {selectedFilter === "monthly" ? (
                  <div className="card mb-2">
                    <div className="card-header bg-transparent p-3-75 border-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className=" mb-0 font-18">{isTitle}</h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-light pull-right shadow-none"
                          onClick={async () => {
                            const now = Date.now();
                            const lastMonthlyFetch = sessionStorage.getItem(
                              LAST_MONTHLY_FETCH_KEY
                            );
                            const timeElapsed =
                              now -
                              (lastMonthlyFetch
                                ? parseInt(lastMonthlyFetch, 10)
                                : 0);

                            if (
                              selectedFilter === "monthly" &&
                              timeElapsed > REFRESH_INTERVAL_MS
                            ) {
                              fetchData(selectedFilter, true);
                            } else if (selectedFilter === "monthly") {
                               setLoadingStatus(prev => ({ ...prev, isLoading: true }));
                              await new Promise((resolve) =>
                                setTimeout(resolve, 1000)
                              );
                               setLoadingStatus(prev => ({ ...prev, isLoading: false }));
                            } else {
                              fetchData(selectedFilter, true);
                            }
                          }}
                        >
                          Refresh <RefreshIcon/>
                        </button>
                      </div>
                    </div>
                    <div className="card-body position-relative">
                      <CardPreLoader isLoading={loadingStatus.isLoading} />
                      <MonthlySaleAnalytics AnalyticsData={AnalyticsData} />
                    </div>
                  </div>
                ) : selectedFilter === "weekly" ? (
                  <>
                    <WeeklySale
                      biWeekData={biWeekData}
                      weeklyHourlyData={weeklyHourlyData}
                      isDayofWeekData={isDayofWeekData}
                      onRefreshBiWeek={fetchBiWeekData}
                      onRefreshWeeklyHourly={fetchWeeklyHourlyData}
                      loading={loadingStatus}
                      onRefreshWeeklydayofweek={fetchDayOfWeekData}
                      fetchStatus={fetchStatus}
                    />
                  </>
                ) : (
                  <div className="card mb-2">
                    <div className="card-header bg-transparent p-3-75 border-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className=" mb-0 font-18">{isTitle}</h5>
                        {!loadingStatus.isLoading &&
                          !fetchStatus.sale && (
                            <div
                              className="d-flex flex-column justify-content-center align-items-center w-100 h-100"
                              style={{
                                backgroundColor: "rgb(255 255 255 / 62%)",
                                position: "absolute",
                                top: 0,
                                left: 0,
                                zIndex: 2,
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-light pull-right font-20"
                                onClick={() => fetchData(selectedFilter, true)}
                              >
                                Refresh{" "}
                                <RefreshIcon iClass={"font-24"}/>
                              </button>
                            </div>
                          )}
                        <button
                          type="button"
                          className="btn btn-sm btn-light pull-right shadow-none"
                          onClick={handleRefresh}
                        >
                          Refresh <RefreshIcon/>
                        </button>
                      </div>
                    </div>
                    <div className="card-body position-relative">
                      <CardPreLoader isLoading={loadingStatus.isLoading} />
                      <SaleAnalytics AnalyticsData={AnalyticsData} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Analytics;
